import { Pipe, PipeTransform } from '@angular/core';
import * as moment from 'moment';

type Status = 'not_started' | 'in_progress' | 'finished' | 'invalid_range';

@Pipe({
    standalone: true,
    name: 'dateDiffInPercent',
})
export class DateDiffInPercentPipe implements PipeTransform {
    transform(
        start: Date | string | moment.Moment,
        end: Date | string | moment.Moment
    ): {
        percent: number;
        elapsedDays: number | null;
        remainingDays: number | null;
        totalDays: number | null;
        status: Status;
    } {
        if (!start || !end) {
            return { percent: 0, elapsedDays: null, remainingDays: null, totalDays: null, status: 'invalid_range' };
        }

        const s = moment(start);
        const e = moment(end);
        const now = moment();

        if (!s.isValid() || !e.isValid()) {
            return { percent: 0, elapsedDays: null, remainingDays: null, totalDays: null, status: 'invalid_range' };
        }

        if (now.isAfter(e)) {
            const totalDays = Math.round(moment.duration(e.diff(s)).asDays());
            return { percent: 100, elapsedDays: totalDays, remainingDays: 0, totalDays, status: 'finished' };
        }

        if (now.isBefore(s)) {
            const totalDays = Math.round(moment.duration(e.diff(s)).asDays());
            const remainingDays = Math.ceil(moment.duration(e.diff(now)).asDays());
            return { percent: 0, elapsedDays: 0, remainingDays, totalDays, status: 'not_started' };
        }

        // --- Percentage Calculation (uses exact values) ---
        const totalMs = e.valueOf() - s.valueOf();
        const elapsedMs = now.valueOf() - s.valueOf();
        const rawPercent = totalMs > 0 ? (elapsedMs / totalMs) * 100 : 100;
        const percent = Math.max(0, Math.min(100, rawPercent));

        // --- Day Calculation (uses rounded values for display) ---
        const totalDays = Math.round(e.diff(s, 'days', true));
        const elapsedDays = Math.floor(now.diff(s, 'days', true));
        const remainingDays = totalDays - elapsedDays;

        const status: Status = percent <= 0 ? 'not_started' : percent >= 100 ? 'finished' : 'in_progress';

        return {
            percent,
            elapsedDays,
            remainingDays,
            totalDays,
            status,
        };
    }
}
